import { ENDPOINTS } from 'config/endpoints';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { PatchTemplateInputs } from 'crono-fe-common/types/DTO/settingsTemplate';
import { useBackgroundInvalidateVoiceNoteCache } from 'crono-fe-common/hooks/crono-extension/gateway';
import { templateByIdQueryKey } from './useGetTemplateById';

type PatchTemplateVars = PatchTemplateInputs;

export default function usePatchTemplate(skipInvalidation?: boolean) {
  const queryClient = useQueryClient();

  const patchTemplate = (templateInput: PatchTemplateVars) => {
    const request: Request = {
      url: `${ENDPOINTS.template.main}`,
      config: {
        method: 'patch',
        data: { data: templateInput },
      },
    };
    return client(request);
  };

  const { call: invalidateVoiceNoteCache } =
    useBackgroundInvalidateVoiceNoteCache();

  const { data, mutate, ...rest } = useMutation<
    Response<null>,
    CronoError,
    PatchTemplateVars
  >(patchTemplate, {
    onSettled: async (_data, _error, variables) => {
      if (skipInvalidation) {
        return;
      }

      if (variables.recordingDuration || variables.recording) {
        await invalidateVoiceNoteCache({ templateId: variables.id });
      }
      queryClient.invalidateQueries([ENDPOINTS.template.main]);
      queryClient.invalidateQueries(templateByIdQueryKey(variables.id));
      queryClient.invalidateQueries([ENDPOINTS.strategy.main]);
      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]);
      queryClient.invalidateQueries([ENDPOINTS.template.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
    },
  });

  return { data, mutate, ...rest };
}
